// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { UILayout } from '@/subsystems/layout'
import type { WorkspaceNodes } from '@/subsystems/palette'
import type { WorkspaceConfigSnapshot } from '../types'
import { layoutEvents } from '@/subsystems/layout'
import { apiClient } from '@/app-shell/api/api-client'

interface WorkspaceConfigState extends WorkspaceConfigSnapshot {
  loading: boolean
  error: Error | null
}

interface WorkspaceConfigResult extends WorkspaceConfigState {
  refresh: () => Promise<void>
}

// Query key factory for consistent cache keys
export const workspaceConfigKeys = {
  config: (orgId: string, wsId: string) => ['workspaceConfig', orgId, wsId] as const,
}

export const useWorkspaceConfig = (
  organizationId?: string,
  workspaceId?: string,
  // Connector administrators discover workspaces without membership; the
  // member-gated config endpoint would 403 and trigger the access-denied
  // auto-switch, so those callers disable the fetch entirely.
  enabled: boolean = true,
): WorkspaceConfigResult => {
  const queryClient = useQueryClient()

  // Fetch workspace config (fast - no DB queries for incomplete setup)
  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: workspaceConfigKeys.config(organizationId ?? '', workspaceId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<Record<string, unknown>>(
        `/workspaces/${organizationId}/${workspaceId}`
      )
      return response.data
    },
    enabled: enabled && !!organizationId && !!workspaceId,
    // Don't throw on 403 — let the UI handle access errors gracefully
    // (e.g., after impersonation navigates to an inaccessible workspace via deep link)
    throwOnError: false,
    // Disable global placeholderData — workspace configs are structurally different
    // between workspaces (different applications, layouts, pipelines). Showing the
    // previous workspace's config as placeholder causes old application components
    // to briefly render with the new workspace ID, triggering incorrect API calls.
    placeholderData: undefined,
    staleTime: 5 * 60_000,
  })

  // Subscribe to layout refresh events
  useEffect(() => {
    if (!organizationId || !workspaceId) return

    const unsubscribe = layoutEvents.subscribe(() => {
      queryClient.invalidateQueries({
        queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
      })
    })
    return unsubscribe
  }, [organizationId, workspaceId, queryClient])

  const refresh = useMemo(() => async () => {
    if (!organizationId || !workspaceId) return
    await queryClient.invalidateQueries({
      queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
    })
  }, [organizationId, workspaceId, queryClient])

  return useMemo(() => {
    const error = configError ? (configError instanceof Error ? configError : new Error('Unknown error')) : null

    if (!configData) {
      return {
        layout: null,
        nodes: null,
        applicationSettings: null,
        enablePrefixSetting: null,
        dashboardTitle: null,
        groupDocumentsByExternalId: null,
        connectorIntakeEnabled: null,
        loading: configLoading,
        error,
        refresh,
      }
    }

    return {
      layout: (configData.uiLayout as UILayout | null),
      nodes: (configData.nodes as WorkspaceNodes | null),
      applicationSettings: (configData.applicationSettings as WorkspaceConfigSnapshot['applicationSettings']) ?? null,
      enablePrefixSetting: (configData.enablePrefixSetting as boolean | null | undefined) ?? null,
      dashboardTitle: (configData.dashboardTitle as string | null | undefined) ?? null,
      groupDocumentsByExternalId: (
        configData.groupDocumentsByExternalId as boolean | null | undefined
      ) ?? false,
      connectorIntakeEnabled: (
        configData.connectorIntakeEnabled as boolean | null | undefined
      ) ?? false,
      loading: configLoading,
      error,
      refresh,
    }
  }, [configData, configLoading, configError, refresh])
}
